import { useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import NewSideBar from '../../components/Navigation/NewSideBar';
import Select from '../../components/forms/Select/Select';
import { LayoutContext } from '../../utils/contexts/LayoutContext';
import { fetchAgents, fetchUsers, postSession } from '../../services/Interviews/sessionService';

export default function CreateSession({ embedded = false }) {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const outletContext = useOutletContext();
  const interview = outletContext?.interview;
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const setPadding = useContext(LayoutContext)?.setPadding;
  const { control, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: { selectedUsers: [], selectedAgent: '' },
  });
  const selectedUsers = watch('selectedUsers');

  useEffect(() => {
    if (embedded || !setPadding) return undefined;
    setPadding('p-0');
    return () => setPadding('p-4');
  }, [embedded, setPadding]);

  const loadOptions = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    const [agentResult, userResult] = await Promise.all([
      fetchAgents({ signal }),
      fetchUsers({ signal }),
    ]);
    if (signal.aborted) return;
    if (!agentResult.success || !userResult.success) {
      setError('Students or AI presenters could not be loaded.');
      setLoading(false);
      return;
    }
    setAgents((agentResult.data?.results || []).map((agent) => ({ value: agent.uuid, label: agent.name })));
    setUsers((userResult.data?.results || []).filter((user) => user.roles?.some((role) => role.code === 'STD')).map((user) => ({
      value: user.uuid,
      label: `${[user.first_name, user.middle_name, user.last_name].filter(Boolean).join(' ') || 'Unnamed student'} (${user.email})`,
    })));
    setLoading(false);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadOptions(controller.signal);
    return () => controller.abort();
  }, [loadOptions]);

  const onSubmit = async ({ selectedUsers: students, selectedAgent }) => {
    setSubmitting(true);
    setError('');
    const results = await Promise.allSettled(students.map((person) => postSession({ interview: uuid, person, agent: selectedAgent })));
    const succeeded = results.filter((result) => result.status === 'fulfilled').length;
    const failed = results.length - succeeded;
    setSubmitting(false);
    if (succeeded) toast.success(`Created ${succeeded} student session${succeeded === 1 ? '' : 's'}.`);
    if (failed) {
      const message = `${failed} session${failed === 1 ? '' : 's'} could not be created. The selected student may already be assigned or unavailable.`;
      if (succeeded) toast.error(message);
      else setError(message);
      if (!succeeded) return;
    }
    navigate(`/organization/interviews/${uuid}/sessions`);
    outletContext?.refreshInterview?.();
  };

  return (
    <div className={`flex h-full ${embedded ? '' : 'max-h-main bg-gray-50 dark:bg-gray-900'}`}>
      {!embedded && <NewSideBar />}
      <main className={`w-full overflow-y-auto ${embedded ? '' : 'p-6 lg:p-8'}`}>
        <div className="mx-auto max-w-4xl space-y-6">
          <header>
            <Link to={`/organization/interviews/${uuid}/sessions`} className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600"><ArrowLeftIcon className="h-4 w-4" />Back to sessions</Link>
            <p className="mt-5 text-sm font-semibold uppercase tracking-[0.16em] text-blue-600">Participant assignment</p>
            <h1 className="mt-2 text-3xl font-bold">Create student sessions</h1>
            <p className="mt-2 text-gray-500">Assign one or several students to {interview?.name || 'this interview'} using the same AI presenter.</p>
          </header>

          {error && <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-800"><p>{error}</p>{!users.length && <button type="button" onClick={() => { const controller = new AbortController(); loadOptions(controller.signal); }} className="mt-2 font-semibold underline">Try loading options again</button>}</div>}

          <div className="grid gap-5 md:grid-cols-3">
            {[['1', 'Choose students', 'Search and select every student who should take this interview.'], ['2', 'Choose presenter', 'One AI presenter will guide all newly created sessions.'], ['3', 'Share access', 'Copy individual access links from the Sessions tab after creation.']].map(([number, title, description]) => <article key={number} className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800"><span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 font-bold text-blue-700">{number}</span><h2 className="mt-3 font-bold">{title}</h2><p className="mt-1 text-sm text-gray-500">{description}</p></article>)}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="space-y-6">
              <Controller name="selectedUsers" control={control} rules={{ validate: (value) => value?.length > 0 || 'Select at least one student.' }} render={({ field }) => <Select label="Students" placeholder="Search students by name or email" options={users} value={field.value} onChange={(options) => field.onChange((options || []).map((option) => option.value))} isSearchable isMulti closeMenuOnSelect={false} loading={loading} required errorMsg={errors.selectedUsers?.message} className="w-full" />} />
              <Controller name="selectedAgent" control={control} rules={{ required: 'Select an AI presenter.' }} render={({ field }) => <Select label="AI presenter" placeholder="Choose the presenter for these sessions" options={agents} value={field.value} onChange={(option) => field.onChange(option?.value || '')} isSearchable loading={loading} required errorMsg={errors.selectedAgent?.message} className="w-full" />} />
              <div className="rounded-xl bg-blue-50 p-4 text-sm text-blue-900 dark:bg-blue-950 dark:text-blue-100"><div className="flex items-start gap-2"><CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0" /><p><strong>{selectedUsers.length || 0} session{selectedUsers.length === 1 ? '' : 's'}</strong> will be created. Students will only see approved evaluation results after completing their interviews.</p></div></div>
            </div>
            <div className="mt-7 flex flex-col-reverse justify-end gap-3 border-t border-gray-200 pt-5 dark:border-gray-700 sm:flex-row"><Link to={`/organization/interviews/${uuid}/sessions`} className="rounded-xl border border-gray-300 px-5 py-2.5 text-center font-semibold hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700">Cancel</Link><button type="submit" disabled={submitting || loading} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"><UserPlusIcon className="h-5 w-5" />{submitting ? 'Creating sessions…' : `Create ${selectedUsers.length || ''} session${selectedUsers.length === 1 ? '' : 's'}`}</button></div>
          </form>
        </div>
      </main>
    </div>
  );
}
